<template>
  <div class="superuser-page">
    <div class="superuser-page__inner">

      <!-- Header -->
      <div class="superuser-page__header">
        <button
          v-if="selectedCompany"
          class="superuser-page__back"
          @click="clearCompany"
        >
          <i class="mdi mdi-arrow-left"></i> All Tenants
        </button>
        <h1 class="superuser-page__title">
          {{ selectedCompany ? selectedCompany.name : 'Super User Console' }}
        </h1>
        <p class="superuser-page__sub">
          {{ selectedCompany ? 'Click any user to log in as them.' : 'Select a tenant to view their org structure.' }}
        </p>
      </div>

      <!-- Company List -->
      <div v-if="!selectedCompany">
        <div v-if="loadingCompanies" class="superuser-page__loading">
          <b-loading :active="true" :is-full-page="false" />
        </div>
        <div v-else>
          <div class="superuser-page__list-header">
            <span v-if="companies.length > 0" class="superuser-page__tenant-count">
              {{ companies.length }} tenant{{ companies.length !== 1 ? 's' : '' }}
            </span>
            <button class="superuser-page__onboard-btn" @click="showOnboardModal = true">
              <i class="mdi mdi-plus"></i> Onboard a Client
            </button>
          </div>
          <div v-if="companies.length === 0" class="superuser-page__empty">
            No tenants found. Onboard your first client to get started.
          </div>
          <div v-else class="superuser-page__company-list">
            <button
              v-for="company in companies"
              :key="company._id"
              class="superuser-page__company-btn"
              @click="selectCompany(company)"
            >
              <i class="mdi mdi-office-building"></i>
              {{ company.name }}
              <span v-if="company.handle && company.handle.length" class="superuser-page__company-handle">{{ Array.isArray(company.handle) ? company.handle.join(', ') : company.handle }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Org Tree -->
      <div v-else>
        <div class="superuser-page__company-actions">
          <button class="superuser-page__action-btn" @click="openEditCompany">
            <i class="mdi mdi-pencil"></i> Edit Company
          </button>
          <button class="superuser-page__action-btn" @click="openModalForExisting(2)">
            <i class="mdi mdi-account-plus"></i> Import Users
          </button>
          <button class="superuser-page__action-btn" @click="openModalForExisting(3)">
            <i class="mdi mdi-sitemap"></i> Edit Org Structure
          </button>
          <button class="superuser-page__action-btn" @click="showMergeModal = true">
            <i class="mdi mdi-link-variant"></i> Manage Merges
          </button>
        </div>
        <div v-if="loadingUsers" class="superuser-page__loading">
          <b-loading :active="true" :is-full-page="false" />
        </div>
        <div v-else-if="orgTree.length === 0" class="superuser-page__empty">
          No users found for this tenant.
        </div>
        <div v-else class="superuser-page__tree">
          <OrgTreeNode
            v-for="root in orgTree"
            :key="root.key"
            :node="root"
            :pending-email="pendingEmail"
            :submitting="impersonating"
            @select="handleSelectUser"
            @toggle-activation="handleToggleActivation"
          />
        </div>
      </div>

      <ManageMergesModal
        v-if="showMergeModal && selectedCompany"
        :company-id="selectedCompany._id"
        :auth-header="authHeader"
        :users="users"
        @close="showMergeModal = false"
        @done="onMergeDone"
      />

      <OnboardClientModal
        v-if="showOnboardModal"
        :auth-header="authHeader"
        :existing-company-id="onboardExistingCompanyId"
        :initial-step="onboardInitialStep"
        :edit-mode="onboardEditMode"
        :company-handles="selectedCompany ? (Array.isArray(selectedCompany.handle) ? selectedCompany.handle : (selectedCompany.handle ? [selectedCompany.handle] : [])) : []"
        @close="showOnboardModal = false"
        @done="onOnboardDone"
      />

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import OrgTreeNode from '@/components/auth/OrgTreeNode.vue';
import OnboardClientModal from '@/components/superuser/OnboardClientModal.vue';
import ManageMergesModal from '@/components/superuser/ManageMergesModal.vue';
import { useUserStore } from '@/stores/userStore';

function buildOrgTree(users) {
  // Exclude secondary merged users — they're represented by their primary
  const visibleUsers = users.filter(u => !u.isSecondaryUser);

  const byId = {};
  for (const u of visibleUsers) {
    byId[u._id] = {
      key: u._id,
      managerKey: u.manager ? String(u.manager) : null,
      persona: {
        ...u,
        title: u.resumeAnalysis?.summary || u.circleFields?.headline || null,
      },
      children: [],
    };
  }

  const roots = [];
  for (const node of Object.values(byId)) {
    if (node.managerKey && byId[node.managerKey]) {
      byId[node.managerKey].children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

export default {
  name: 'SuperUser',
  components: { OrgTreeNode, OnboardClientModal, ManageMergesModal },
  data() {
    return {
      companies: [],
      loadingCompanies: false,
      selectedCompany: null,
      users: [],
      loadingUsers: false,
      pendingEmail: '',
      impersonating: false,
      showOnboardModal: false,
      onboardExistingCompanyId: null,
      onboardInitialStep: 1,
      onboardEditMode: false,
      showMergeModal: false,
    };
  },
  computed: {
    orgTree() {
      return buildOrgTree(this.users);
    },
    authHeader() {
      return { Authorization: this.$cookies.get('teamwrkr-token') };
    },
  },
  mounted() {
    this.loadCompanies();
  },
  methods: {
    async loadCompanies() {
      this.loadingCompanies = true;
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_URL}/superuser/companies`,
          { headers: this.authHeader }
        );
        this.companies = res.data;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load tenants.', type: 'is-danger' });
      } finally {
        this.loadingCompanies = false;
      }
    },

    async selectCompany(company) {
      this.selectedCompany = company;
      this.users = [];
      this.loadingUsers = true;
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_URL}/superuser/companies/${company._id}/users`,
          { headers: this.authHeader }
        );
        this.users = res.data;
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load org structure.', type: 'is-danger' });
      } finally {
        this.loadingUsers = false;
      }
    },

    clearCompany() {
      this.selectedCompany = null;
      this.users = [];
      this.pendingEmail = '';
    },

    openEditCompany() {
      this.onboardExistingCompanyId = this.selectedCompany._id;
      this.onboardInitialStep = 1;
      this.onboardEditMode = true;
      this.showOnboardModal = true;
    },

    openModalForExisting(step) {
      this.onboardExistingCompanyId = this.selectedCompany._id;
      this.onboardInitialStep = step;
      this.onboardEditMode = false;
      this.showOnboardModal = true;
    },

    onOnboardDone() {
      this.showOnboardModal = false;
      this.onboardExistingCompanyId = null;
      this.onboardInitialStep = 1;
      this.onboardEditMode = false;
      if (this.selectedCompany) {
        this.selectCompany(this.selectedCompany);
      } else {
        this.loadCompanies();
      }
    },

    onMergeDone() {
      this.showMergeModal = false;
      if (this.selectedCompany) this.selectCompany(this.selectedCompany);
    },

    async handleToggleActivation({ userId, isAccountActivated }) {
      const target = this.users.find(u => u._id === userId);
      const previousValue = target ? target.isAccountActivated : undefined;
      if (target) target.isAccountActivated = isAccountActivated;

      try {
        await axios.patch(
          `${process.env.VUE_APP_API_URL}/superuser/users/${userId}`,
          { isAccountActivated },
          { headers: this.authHeader }
        );
      } catch (err) {
        if (target) target.isAccountActivated = previousValue;
        const msg = err?.response?.data?.message || 'Failed to update account activation.';
        this.$buefy.toast.open({ message: msg, type: 'is-danger' });
      }
    },

    async handleSelectUser(persona) {
      if (this.impersonating) return;
      this.pendingEmail = persona.email;
      this.impersonating = true;
      let started = false;
      try {
        const res = await axios.post(
          `${process.env.VUE_APP_API_URL}/superuser/impersonate`,
          { targetUserId: persona._id },
          { headers: this.authHeader }
        );
        const { token, targetUser } = res.data;
        const userStore = useUserStore();
        userStore.startImpersonation(token, {
          ...targetUser,
          companyName: this.selectedCompany?.name || '',
        });
        started = true;
        // Fetch the impersonated user's full profile so userStore.user updates
        await userStore.fetchUser(token, { force: true });
        this.$router.push('/');
      } catch (err) {
        // spec-ask-ux-impersonation-and-composer: impersonation started but loading the target
        // failed — undo it (back to the super user's own token) rather than leave a half-started
        // impersonation behind.
        if (started) {
          try {
            await useUserStore().stopImpersonation();
          } catch {
            // the error toast below still tells the super user it failed
          }
        }
        const msg = err?.response?.data?.message || 'Failed to impersonate user.';
        this.$buefy.toast.open({ message: msg, type: 'is-danger' });
        this.pendingEmail = '';
      } finally {
        this.impersonating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.superuser-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.superuser-page__inner {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.superuser-page__header {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.superuser-page__back {
  background: none;
  border: none;
  color: $color-primary;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0;
  margin-bottom: 0.3rem;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
}

.superuser-page__title {
  font-size: 1.8rem;
  font-weight: 800;
  color: $color-text;
  margin: 0;
}

.superuser-page__sub {
  font-size: 1rem;
  color: $color-text-muted;
  margin: 0;
}

.superuser-page__loading {
  position: relative;
  min-height: 80px;
}

.superuser-page__empty {
  color: $color-text-subtle;
  font-size: 1rem;
  padding: 2rem 0;
}

.superuser-page__company-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.superuser-page__company-btn {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: $color-surface;
  border: 1.5px solid $color-border;
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: $color-text;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;

  i {
    font-size: 1.3rem;
    color: $color-primary;
  }

  &:hover {
    border-color: $color-primary;
    background: $color-surface-alt;
  }
}

.superuser-page__company-handle {
  margin-left: auto;
  font-size: 0.8rem;
  font-weight: 500;
  color: $color-text-subtle;
  font-family: monospace;
}

.superuser-page__company-actions {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.superuser-page__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: transparent;
  color: $color-primary;
  border: 1.5px solid $color-primary;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  i { font-size: 1rem; }

  &:hover { background: rgba($color-primary, 0.08); }
}

.superuser-page__list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.superuser-page__tenant-count {
  font-size: 0.85rem;
  color: $color-text-muted;
}

.superuser-page__onboard-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: $color-primary;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.1rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s;

  i { font-size: 1rem; }

  &:hover { filter: brightness(1.08); }
}

.superuser-page__tree {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
